import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { IAppState } from "@multiweb/types/react";
import { logger } from "../logger";
import { getOpenedFormInfo } from "../openedItems";
import { createGetFormNameMap, getFormItems, getProfiles, getTextCompareFn } from "../selectors";
import { IsFormItemOrInstanceFavoriteSelectorParams, MenuItemActivatedType } from "../types";
import { FavoriteMenuItem, FavoriteMenuItems } from "./types";

/**
 * Získá z globálního stavu podstrom pro oblíbené položky.
 */
const getModel = (state: IAppState) => state.menu.favoriteItems;

/**
 * Získá id položek menu oblíbených formulářů.
 * @param state
 */
const getFavoriteForms: Selector<IAppState, FavoriteMenuItems> = (state) => getModel(state).favoriteForms;

/**
 * Zjistí, zda je formulář se zadaným kódem oblíbený.
 * @param state
 * @param formCode Kód formuláře.
 * @param profileId Id profilu
 */
export const isFormFavorite: Selector<IAppState, boolean, FavoriteMenuItem> = (state, { formId, profileId }) =>
    getModel(state).favoriteForms.some(favoriteItem => favoriteItem.formId === formId && favoriteItem.profileId == profileId);

/**
 * Zjistí, zda položka nebo instance formuláře patří mezi oblíbené.
 *
 * @param state
 * @param itemId Id položky nebo instance formuláře.
 * @param type Typ položky.
 * @param profileId Id profilu
 */
export const isFormItemOrInstanceFavorite: Selector<IAppState, boolean, IsFormItemOrInstanceFavoriteSelectorParams> = (state, { formId, type, profileId }) => {
    if (type === MenuItemActivatedType.formFavoriteActionItem) {
        const formIdNumber = formId as number;
        return isFormFavorite(state, { formId: formIdNumber, profileId });
    }
    if (type === MenuItemActivatedType.openedInstanceFavoriteActionItem) {
        const openedForm = getOpenedFormInfo(state, formId as string);
        if (openedForm?.menuItemId) {
            return isFormFavorite(state, { formId: openedForm.menuItemId, profileId: openedForm.profileId });
        } else {
            logger.warn("Cannot determine if opened form instance is favorite. Opened form instance with id %s was not found.", formId);
            return false;
        }
    }
    logger.warn("Cannot check if the form is favorite because the item activated type %s is invalid", type);
    return false;
};

/**
 * Získá ids všech položek menu, jejichž formulář je oblíbený. Selector je cachovaný.
 */
export const getFavoriteMenuItemIds = createSelector(
    getFavoriteForms,
    getFormItems,
    (favoriteForms, formItems) => favoriteForms.filter((favoriteFormId) => !!formItems[favoriteFormId.formId]),
);

/**
 * Pomocný selector pro vytvoření mapování mezi id oblíbených položek menu a jejich lokalizovanými názvy.
 * Tento selector vrací selector pro získání mapování. Vytváří nový selector pokud se změní oblíbené formuláře.
 * @param state
 */
const getFavoriteMenuItemsNameMap = createSelector(
    getFavoriteMenuItemIds,
    (favoriteMenuItemIds) => createGetFormNameMap(Array.from(favoriteMenuItemIds, favoriteItem => favoriteItem.formId)),
);

/**
 * Získá seznam oblíbených položek menu, které jsou seřazeny podle názvu. Selektor je cachovaný.
 * @param state
 */
export const getOrderedFavoriteMenuItems = createSelector(
    (state: IAppState) => getFavoriteMenuItemsNameMap(state)(state),
    getProfiles,
    getFormItems,
    getFavoriteForms,
    getTextCompareFn,
    (localizedFormObject, profiles, forms, favoriteItems, textCompareFn) => {
        const favoriteMenuItems = favoriteItems.filter(item => !!forms[item.formId] && ((item.profileId != undefined && !!profiles[item.profileId]) || item.profileId == undefined));
        const entries: [FavoriteMenuItem, string][] = favoriteMenuItems.map(favoriteItem => favoriteItem.profileId == undefined ? [favoriteItem, localizedFormObject[favoriteItem.formId]] : [favoriteItem, profiles[favoriteItem.profileId]?.title]);
        return entries.sort(([_favoriteItem1, localizedName1]: [any, string], [_favoriteItem2, localizedName2]: [any, string]) => textCompareFn(localizedName1, localizedName2))
            // Identifikátory formulářů a profilů jsou použity jako klíče objektu – jako klíč však není možné použít číslo a proto je nutná zpětná konverze.
            .map(([favoriteItem, _localizedName]) => favoriteItem);
    },
);

/**
 * Zjistí, zda je pro zkoumanou instanci formuláře povolena akce oblíbených.
 *
 * Prozatím je v rámci 27523 domluveno, že správné identifikování podporovaných typů formulářů je příliš složité a nevyplatí se řešit.
 * Proto zůstanou oblíbené akce pro všechny formuláře bez položky v menu zakázané. Proto se tu vrací vždy false. Selector se zatím ponechá pro případné budoucí řešení.
 * @param _state
 * @param _formInstanceId Id instance otevřeného formuláře.
 */
export const isFormItemInstanceFavoriteActionEnabled: Selector<IAppState, boolean, string> = (_state, _formInstanceId) => false;
